/**
 * Org-scoped ingest source badge for Control Centre tab headers.
 * Place top-right. Static label for a single source; dropdown when the org
 * has multiple telematics providers the user can filter by.
 */

export type ControlCentreSourceOption = {
  value: string;
  label: string;
};

interface ControlCentreSourceBadgeProps {
  /** Display label when not using a select (e.g. "Palletways") */
  label?: string | null;
  className?: string;
  /** Prefix before the source name. Default "Source:" (CC tabs); Profit uses "Data Sources:". */
  prefixLabel?: string;
  /** When length > 1, render as a Source: dropdown */
  options?: ControlCentreSourceOption[];
  value?: string;
  onChange?: (value: string) => void;
  selectId?: string;
}

const badgeClass =
  'inline-flex items-center shrink-0 px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider bg-ring/15 text-ring border border-ring/40';

export default function ControlCentreSourceBadge({
  label,
  className = '',
  prefixLabel = 'Source:',
  options,
  value,
  onChange,
  selectId = 'control-centre-source',
}: ControlCentreSourceBadgeProps) {
  const selectable = Array.isArray(options) && options.length > 1 && typeof onChange === 'function';

  if (selectable) {
    const selectedLabel =
      options.find((o) => o.value === value)?.label
      || options[0]?.label
      || '';

    return (
      <label
        className={`${badgeClass} gap-1.5 cursor-pointer ${className}`}
        title={`${prefixLabel} ${selectedLabel}`}
      >
        <span className="text-ring/80 font-semibold normal-case tracking-normal">{prefixLabel}</span>
        <select
          id={selectId}
          value={value ?? options[0]?.value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-transparent text-ring text-xs font-bold uppercase tracking-wider border-0 outline-none cursor-pointer pr-1 max-w-56"
          aria-label={prefixLabel.replace(/:$/, '') || 'Data source'}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-popover text-white normal-case">
              {opt.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  const text = String(label || options?.[0]?.label || '').trim();
  if (!text) return null;

  return (
    <span
      className={`${badgeClass} gap-1.5 ${className}`}
      title={`${prefixLabel} ${text}`}
    >
      <span className="text-ring/80 font-semibold normal-case tracking-normal">{prefixLabel}</span>
      <span>{text}</span>
    </span>
  );
}
